import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "state-diagram" }
const _hoisted_2 = { class: "transitions" }
const _hoisted_3 = ["d", "marker-end"]
const _hoisted_4 = { class: "nodes" }
const _hoisted_5 = ["transform"]
const _hoisted_6 = ["width", "filter"]
const _hoisted_7 = ["x", "y"]

import { computed, watch, ref } from "vue";
import type { AIAssistantState } from "@/composables/useStateMachine";

const svgWidth = 340;
const svgHeight = 420;
const nodeHeight = 36;

export default /*@__PURE__*/_defineComponent({
  __name: 'StateDiagram',
  props: {
    currentState: {},
    previousState: {}
  },
  setup(__props: any) {

const props = __props;

// Track previous state for transition highlighting
const lastTransition = ref<{ from: AIAssistantState; to: AIAssistantState } | null>(null);

watch(
  () => props.currentState,
  (newState, oldState) => {
    if (oldState && newState !== oldState) {
      lastTransition.value = { from: oldState as AIAssistantState, to: newState };
      // Clear transition highlight after animation
      setTimeout(() => {
        lastTransition.value = null;
      }, 2000);
    }
  }
);

// SVG dimensions
const viewBox = `0 0 ${svgWidth} ${svgHeight}`;

// State node positions and sizes
const states = computed(() => [
  { id: "idle" as const, label: "idle", x: 120, y: 15, width: 100 },
  { id: "form_template_selection" as const, label: "form_template_selection", x: 50, y: 75, width: 240 },
  { id: "device_selection" as const, label: "device_selection", x: 80, y: 135, width: 180 },
  { id: "recording" as const, label: "recording", x: 110, y: 195, width: 120 },
  { id: "processing" as const, label: "processing", x: 110, y: 255, width: 120 },
  { id: "error" as const, label: "error", x: 255, y: 255, width: 70 },
  { id: "review_ready" as const, label: "review_ready", x: 95, y: 315, width: 150 },
]);

// Helper to get state node position
const getStatePos = (stateId: AIAssistantState) => {
  const state = states.value.find((s) => s.id === stateId);
  if (!state) return { x: 0, y: 0, width: 100 };
  return state;
};

// Valid transitions from the state machine
const validTransitions: Array<{ from: AIAssistantState; to: AIAssistantState }> = [
  // From idle
  { from: "idle", to: "form_template_selection" },
  { from: "idle", to: "processing" },
  { from: "idle", to: "review_ready" },
  // From form_template_selection
  { from: "form_template_selection", to: "device_selection" },
  { from: "form_template_selection", to: "idle" },
  // From device_selection
  { from: "device_selection", to: "recording" },
  { from: "device_selection", to: "idle" },
  // From recording
  { from: "recording", to: "processing" },
  { from: "recording", to: "idle" },
  // From processing
  { from: "processing", to: "review_ready" },
  { from: "processing", to: "error" },
  // From review_ready
  { from: "review_ready", to: "idle" },
  { from: "review_ready", to: "form_template_selection" },
  // From error
  { from: "error", to: "idle" },
  { from: "error", to: "form_template_selection" },
];

// Generate path for a transition
const generatePath = (from: AIAssistantState, to: AIAssistantState): string => {
  const fromState = getStatePos(from);
  const toState = getStatePos(to);

  // Calculate center points
  const fromCenterX = fromState.x + fromState.width / 2;
  const fromCenterY = fromState.y + nodeHeight / 2;
  const toCenterX = toState.x + toState.width / 2;
  const toCenterY = toState.y + nodeHeight / 2;

  // Determine connection points based on relative positions
  let startX: number, startY: number, endX: number, endY: number;

  // Forward flow (downward)
  if (toState.y > fromState.y) {
    startX = fromCenterX;
    startY = fromState.y + nodeHeight;
    endX = toCenterX;
    endY = toState.y;
  }
  // Backward flow (upward) - return paths
  else if (toState.y < fromState.y) {
    // Route around the left side for return paths
    startX = fromState.x;
    startY = fromCenterY;
    endX = toState.x;
    endY = toCenterY;

    // Create a curved path going left then up
    const offsetX = -30;
    return `M ${startX} ${startY} 
            C ${startX + offsetX} ${startY}, 
              ${endX + offsetX} ${endY}, 
              ${endX} ${endY}`;
  }
  // Same row (horizontal) - for error transition
  else {
    if (toState.x > fromState.x) {
      startX = fromState.x + fromState.width;
      startY = fromCenterY;
      endX = toState.x;
      endY = toCenterY;
    } else {
      startX = fromState.x;
      startY = fromCenterY;
      endX = toState.x + toState.width;
      endY = toCenterY;
    }
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }

  // Default: straight line with slight curve for forward paths
  const midY = (startY + endY) / 2;
  return `M ${startX} ${startY} 
          C ${startX} ${midY}, 
            ${endX} ${midY}, 
            ${endX} ${endY}`;
};

// Check if this is a "return" transition (going backwards/upwards)
const isReverseTransition = (from: AIAssistantState, to: AIAssistantState): boolean => {
  const fromState = getStatePos(from);
  const toState = getStatePos(to);
  return toState.y < fromState.y;
};

// Compute all transition paths
const transitions = computed(() =>
  validTransitions.map((t) => ({
    from: t.from,
    to: t.to,
    path: generatePath(t.from, t.to),
    isReverse: isReverseTransition(t.from, t.to),
  }))
);

// Check if a transition is the currently active one
const isActiveTransition = (from: AIAssistantState, to: AIAssistantState): boolean => {
  if (!lastTransition.value) return false;
  return lastTransition.value.from === from && lastTransition.value.to === to;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createElementBlock("svg", {
      viewBox: viewBox,
      class: "diagram-svg"
    }, [
      _cache[0] || (_cache[0] = _createStaticVNode("<defs data-v-2f54b680><marker id=\"arrowhead\" markerWidth=\"10\" markerHeight=\"7\" refX=\"9\" refY=\"3.5\" orient=\"auto\" data-v-2f54b680><polygon points=\"0 0, 10 3.5, 0 7\" fill=\"#404060\" data-v-2f54b680></polygon></marker><marker id=\"arrowhead-active\" markerWidth=\"10\" markerHeight=\"7\" refX=\"9\" refY=\"3.5\" orient=\"auto\" data-v-2f54b680><polygon points=\"0 0, 10 3.5, 0 7\" fill=\"#60a5fa\" data-v-2f54b680></polygon></marker><filter id=\"glow\" x=\"-50%\" y=\"-50%\" width=\"200%\" height=\"200%\" data-v-2f54b680><feGaussianBlur stdDeviation=\"3\" result=\"coloredBlur\" data-v-2f54b680></feGaussianBlur><feMerge data-v-2f54b680><feMergeNode in=\"coloredBlur\" data-v-2f54b680></feMergeNode><feMergeNode in=\"SourceGraphic\" data-v-2f54b680></feMergeNode></feMerge></filter><filter id=\"glow-green\" x=\"-50%\" y=\"-50%\" width=\"200%\" height=\"200%\" data-v-2f54b680><feGaussianBlur stdDeviation=\"4\" result=\"coloredBlur\" data-v-2f54b680></feGaussianBlur><feMerge data-v-2f54b680><feMergeNode in=\"coloredBlur\" data-v-2f54b680></feMergeNode><feMergeNode in=\"SourceGraphic\" data-v-2f54b680></feMergeNode></feMerge></filter></defs>", 1)),
      _createElementVNode("g", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(transitions.value, (transition, index) => {
          return (_openBlock(), _createElementBlock("path", {
            key: index,
            d: transition.path,
            class: _normalizeClass(["transition-path", {
            active: isActiveTransition(transition.from, transition.to),
            'reverse-path': transition.isReverse,
          }]),
            "marker-end": 
            isActiveTransition(transition.from, transition.to) ? 'url(#arrowhead-active)' : 'url(#arrowhead)'
          
          }, null, 10, _hoisted_3))
        }), 128))
      ]),
      _createElementVNode("g", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, (state) => {
          return (_openBlock(), _createElementBlock("g", {
            key: state.id,
            class: _normalizeClass(["state-node", {
            active: __props.currentState === state.id,
            'is-review-ready': state.id === 'review_ready',
          }]),
            transform: `translate(${state.x}, ${state.y})`
          }, [
            _createElementVNode("rect", {
              width: state.width,
              height: nodeHeight,
              rx: 6,
              ry: 6,
              class: "node-bg",
              filter: __props.currentState === state.id ? (state.id === 'review_ready' ? 'url(#glow-green)' : 'url(#glow)') : ''
            }, null, 8, _hoisted_6),
            _createElementVNode("text", {
              x: state.width / 2,
              y: nodeHeight / 2 + 1,
              class: "node-label"
            }, _toDisplayString(state.label), 9, _hoisted_7)
          ], 10, _hoisted_5))
        }), 128))
      ])
    ]))
  ]))
}
}

})